import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { motion } from 'framer-motion';

const InputArea = forwardRef(({ onSendMessage, disabled }, ref) => {
  const [message, setMessage] = useState('');

  // اجازه می‌دهد App.js متن را از بیرون تنظیم کند
  useImperativeHandle(ref, () => ({
    setInputText: (text) => {
      setMessage(text);
    }
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur sticky bottom-0 left-0 right-0 z-30 p-2 sm:p-3 md:p-4 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="relative">
          <motion.div 
            whileFocus={{ scale: 1.01 }}
            className="relative flex items-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-xl sm:rounded-2xl border-2 border-gray-200 dark:border-gray-700 focus-within:border-purple-500 dark:focus-within:border-purple-500 transition-all shadow-lg"
          >
            {/* Textarea */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="سوال خود را درباره سفر بپرسید..."
              disabled={disabled}
              rows={1}
              className="flex-1 bg-transparent px-3 sm:px-4 md:px-5 py-3 sm:py-3.5 md:py-4 text-sm sm:text-base text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none outline-none min-h-[48px] sm:min-h-[52px] md:min-h-[56px] max-h-32"
              style={{ 
                direction: 'rtl',
                unicodeBidi: 'plaintext'
              }}
            />

            {/* Send Button */}
            <motion.button
              type="submit"
              disabled={!message.trim() || disabled}
              whileHover={{ scale: message.trim() ? 1.05 : 1 }}
              whileTap={{ scale: message.trim() ? 0.95 : 1 }}
              className={`m-1.5 sm:m-2 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all ${
                message.trim() && !disabled
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-500/30'
                  : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
              }`}
            >
              {disabled ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </motion.div>
              ) : (
                <svg 
                  className={`w-4 h-4 sm:w-5 sm:h-5 ${message.trim() ? 'text-white' : 'text-gray-500'}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </motion.button>
          </motion.div>

          {/* Helper Text */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mt-2 text-center"
          >
            با فشردن Enter پیام ارسال می‌شود • Shift+Enter برای خط جدید
          </motion.p>
        </form>
      </div>
    </div>
  );
});

export default InputArea;
